import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle2, AlertCircle, ClipboardCheck, BarChart3, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Progress } from "./ui/progress";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../utils/helpers";

export const QCDashboard = ({ sites, studies }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStudy, setFilterStudy] = useState("all");
  const [filterSite, setFilterSite] = useState("all");
  const [expandedSection, setExpandedSection] = useState("study");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/qc-dashboard`);
      setData(res.data);
    } catch {
      toast.error("Failed to load QC dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-gray-500">
        No QC data available. Create visits to generate QC tasks with subtasks.
      </div>
    );
  }

  const { summary, by_study, by_site, by_assignee, tasks } = data;

  const filteredTasks = tasks.filter((t) => {
    if (filterStudy !== "all" && t.study_id !== filterStudy) return false;
    if (filterSite !== "all" && t.site_id !== filterSite) return false;
    return true;
  });

  const getStatusColor = (pct) => {
    if (pct >= 80) return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (pct >= 50) return "text-amber-700 bg-amber-50 border-amber-200";
    return "text-red-700 bg-red-50 border-red-200";
  };

  const getProgressColor = (pct) => {
    if (pct >= 80) return "[&>div]:bg-emerald-500";
    if (pct >= 50) return "[&>div]:bg-amber-500";
    return "[&>div]:bg-red-500";
  };

  const getTaskStatusBadge = (status) => {
    const map = {
      todo: "bg-slate-100 text-slate-700",
      in_progress: "bg-blue-100 text-blue-700",
      delayed: "bg-red-100 text-red-700",
      completed: "bg-emerald-100 text-emerald-700",
    };
    return map[status] || "bg-gray-100 text-gray-700";
  };

  const SectionToggle = ({ id, title, icon: Icon }) => (
    <button
      data-testid={`qc-section-${id}`}
      onClick={() => setExpandedSection(expandedSection === id ? null : id)}
      className="flex items-center justify-between w-full px-4 py-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-2 font-semibold text-gray-800">
        <Icon className="h-4 w-4 text-teal-600" />
        {title}
      </div>
      {expandedSection === id ? (
        <ChevronUp className="h-4 w-4 text-gray-500" />
      ) : (
        <ChevronDown className="h-4 w-4 text-gray-500" />
      )}
    </button>
  );

  return (
    <div data-testid="qc-dashboard" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">QC Completion Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Track quality check subtask completion across studies, sites, and team members
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-teal-50 to-teal-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-teal-900">QC Tasks</CardTitle>
            <ClipboardCheck className="h-4 w-4 text-teal-600" />
          </CardHeader>
          <CardContent>
            <div data-testid="qc-total-tasks" className="text-3xl font-bold text-teal-900">{summary.total_qc_tasks}</div>
            <p className="text-xs text-teal-600 mt-1">{summary.completed_tasks} completed</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-blue-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-900">Total Checklist Items</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div data-testid="qc-total-subtasks" className="text-3xl font-bold text-blue-900">{summary.total_subtasks}</div>
            <p className="text-xs text-blue-600 mt-1">{summary.completed_subtasks} checked off</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-amber-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-amber-900">Remaining Items</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div data-testid="qc-remaining" className="text-3xl font-bold text-amber-900">{summary.total_subtasks - summary.completed_subtasks}</div>
            <p className="text-xs text-amber-600 mt-1">still to complete</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-emerald-900">Overall Completion</CardTitle>
            <BarChart3 className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div data-testid="qc-overall-pct" className="text-3xl font-bold text-emerald-900">{summary.overall_completion_pct}%</div>
            <Progress value={summary.overall_completion_pct} className={`mt-2 h-2 ${getProgressColor(summary.overall_completion_pct)}`} />
          </CardContent>
        </Card>
      </div>

      {/* By Study */}
      <SectionToggle id="study" title="Completion by Study" icon={BarChart3} />
      {expandedSection === "study" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {by_study.map((s) => (
            <Card key={s.id} className={`border shadow-sm ${getStatusColor(s.completion_pct)}`}>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm truncate max-w-[60%]">{s.name}</span>
                  <Badge variant="outline" className="text-xs font-bold">{s.completion_pct}%</Badge>
                </div>
                <Progress value={s.completion_pct} className={`h-2 ${getProgressColor(s.completion_pct)}`} />
                <div className="flex justify-between text-xs mt-2 opacity-75">
                  <span>{s.completed}/{s.total} items</span>
                  <span>{s.tasks} tasks ({s.tasks_completed} done)</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {by_study.length === 0 && <p className="text-gray-400 text-sm col-span-2 text-center py-4">No study data available</p>}
        </div>
      )}

      {/* By Site */}
      <SectionToggle id="site" title="Completion by Site" icon={BarChart3} />
      {expandedSection === "site" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {by_site.map((s) => (
            <Card key={s.id} className={`border shadow-sm ${getStatusColor(s.completion_pct)}`}>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm truncate max-w-[60%]">{s.name}</span>
                  <Badge variant="outline" className="text-xs font-bold">{s.completion_pct}%</Badge>
                </div>
                <Progress value={s.completion_pct} className={`h-2 ${getProgressColor(s.completion_pct)}`} />
                <div className="flex justify-between text-xs mt-2 opacity-75">
                  <span>{s.completed}/{s.total} items</span>
                  <span>{s.tasks} tasks ({s.tasks_completed} done)</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {by_site.length === 0 && <p className="text-gray-400 text-sm col-span-2 text-center py-4">No site data available</p>}
        </div>
      )}

      {/* By Assignee */}
      <SectionToggle id="assignee" title="Completion by Assignee" icon={BarChart3} />
      {expandedSection === "assignee" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {by_assignee.map((a) => (
            <Card key={a.name} className={`border shadow-sm ${getStatusColor(a.completion_pct)}`}>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{a.name}</span>
                  <Badge variant="outline" className="text-xs font-bold">{a.completion_pct}%</Badge>
                </div>
                <Progress value={a.completion_pct} className={`h-2 ${getProgressColor(a.completion_pct)}`} />
                <div className="flex justify-between text-xs mt-2 opacity-75">
                  <span>{a.completed}/{a.total} items</span>
                  <span>{a.tasks} tasks ({a.tasks_completed} done)</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {by_assignee.length === 0 && <p className="text-gray-400 text-sm col-span-2 text-center py-4">No assignee data available</p>}
        </div>
      )}

      {/* Task Detail Table */}
      <SectionToggle id="tasks" title="Task Details" icon={ClipboardCheck} />
      {expandedSection === "tasks" && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap gap-3 items-center">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={filterStudy} onValueChange={setFilterStudy}>
                <SelectTrigger className="w-48" data-testid="qc-filter-study">
                  <SelectValue placeholder="All Studies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Studies</SelectItem>
                  {studies.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSite} onValueChange={setFilterSite}>
                <SelectTrigger className="w-48" data-testid="qc-filter-site">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">{filteredTasks.length} tasks</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-700">Task</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Visit</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Study</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Site</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Assignee</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Status</th>
                    <th className="text-left p-3 font-semibold text-gray-700 min-w-[160px]">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task, idx) => (
                    <tr key={task.id} className={`border-b hover:bg-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-25"}`}>
                      <td className="p-3 font-medium text-gray-900 max-w-[200px] truncate">{task.title}</td>
                      <td className="p-3 text-gray-600 text-xs max-w-[150px] truncate">{task.visit || "-"}</td>
                      <td className="p-3 text-gray-600 text-xs max-w-[150px] truncate">{task.study}</td>
                      <td className="p-3 text-gray-600 text-xs max-w-[120px] truncate">{task.site}</td>
                      <td className="p-3 text-gray-600 text-xs">{task.assignee}</td>
                      <td className="p-3">
                        <Badge className={`text-xs ${getTaskStatusBadge(task.status)}`}>
                          {task.status.replace("_", " ")}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Progress value={task.completion_pct} className={`h-2 flex-1 ${getProgressColor(task.completion_pct)}`} />
                          <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                            {task.subtask_completed}/{task.subtask_total}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredTasks.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-gray-400">
                        No QC tasks found. Create visits to auto-generate QC checklist tasks.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
